import React, { ReactNode } from "react"
import Select from "components/ReactHookForm/Select"
import TextField from "components/ReactHookForm/TextField"
import DatePickerField from "components/ReactHookForm/DatePickerField"
import { Control } from "react-hook-form"
import { Box, Flex, Stack } from "@chakra-ui/react"
import { Boolean } from "utilities/enums"
import RichTextField, { RichTextFieldType } from "components/ReactHookForm/RichTextField"
import TicketPrice from "../Ticket/TicketPrice"
import GuestCreationCards from "./GuestCreationCards"
import EmailConfirmation from "./EmailConfirmation"
import NameAndDescription from "./NameAndDescription"
import MaxPerOrderField from "./MaxPerOrderField"
import LanguageField from "./LanguageField"

type Props = {
  control: Control<object, any>
  eventId: string
  sendConfirmationEmails: boolean,
  showVisibilityInput?: boolean
  showQuantityInput?: boolean
  quantityHelperText?: ReactNode
  showLimitPerSubmission? : boolean
  showPrice?: boolean
  currency?: string
  showGuestCreation?: boolean
}

const visibilityOptions = [
  { label: "Public", value: Boolean.False },
  { label: "Private", value: Boolean.True },
]

// Width for the wide single fields (no-price Name & Description, Closing Time) so they
// match the Submission Message textarea and don't over-stretch on sparse rows.
const fieldMaxW = "440px"

const BasicInputs = ({
  control,
  eventId,
  sendConfirmationEmails,
  showVisibilityInput,
  showQuantityInput,
  quantityHelperText = "The form will close after the quantity is reached.",
  showLimitPerSubmission,
  showPrice,
  currency,
  showGuestCreation,
}: Props) => (
  <Stack spacing={8} pt={2}>
    {/* Row 1: Name & Description | Ticket Price | Language.
        Without a price, cap Name/Description at fieldMaxW (the Submission Message width).
        Language sits 3rd next to Price on Ticket, 2nd (next to Name/Description) elsewhere. */}
    <Flex gap={8} align="flex-start">
      <Box flex={2} maxW={showPrice ? undefined : fieldMaxW}>
        <NameAndDescription control={control} />
      </Box>
      {showPrice && (
        <Box flex={1}>
          <TicketPrice control={control} currency={currency ?? ""} />
        </Box>
      )}
      <Box flex={1}>
        <LanguageField control={control} eventId={eventId} />
      </Box>
    </Flex>

    <Flex gap={6} align="flex-start">
      {showVisibilityInput && (
        <Box flex={1}>
          <Select
            name="private"
            control={control}
            label="Visibility"
            options={visibilityOptions}
            helperText="Make this “Private” to hide it from the Guest Portal listing."
            vertical
            fullWidth
          />
        </Box>
      )}
      <Box flex={1.4} maxW={fieldMaxW}>
        <DatePickerField
          showTimeSelect
          renderInPortal
          popperPlacement="bottom-start"
          name="submissionsEndAt"
          control={control}
          label="Closing Time"
          variant="outline"
          helperText="The form will close on this date. You can clear this date if you don't want the form to close, even after your event ends."
          showDeleteButton
          vertical
        />
      </Box>
      {showQuantityInput && (
        <Box flex={1}>
          <TextField
            name="quantity"
            control={control}
            label="Quantity"
            variant="outline"
            placeholder="Unlimited"
            helperText={quantityHelperText}
            type="number"
            showDeleteButton
            vertical
          />
        </Box>
      )}
      {showLimitPerSubmission && (
        <Box flex={1}>
          <MaxPerOrderField control={control} label={`Max per ${showPrice ? "Order" : "Submission"}`} />
        </Box>
      )}
    </Flex>

    {/* Row 3: Guest Creation cards */}
    {showGuestCreation && <GuestCreationCards control={control} />}

    {/* Row 4: Submission Message | Email Confirmation */}
    <Flex gap={8} align="flex-start">
      <Box flex={1.4}>
        <RichTextField
          label="Submission Message"
          name="submissionMessage"
          helperText="Provide a message that will be displayed on the confirmation page once the form has been successfully submitted."
          control={control}
          type={RichTextFieldType.FORM_BLOCK_TEXT}
          vertical
        />
      </Box>
      <Box flex={1}>
        <EmailConfirmation control={control} sendConfirmationEmails={sendConfirmationEmails} />
      </Box>
    </Flex>
  </Stack>
)

export default BasicInputs
